import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

const STORAGE_PREFIX = 'voice_feature_promo_v1';
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

function storageKey(user) {
  const cid = user?.company_id ?? 'x';
  const type = user?.type || 'user';
  const uid = user?.id ?? '0';
  return `${STORAGE_PREFIX}:${cid}:${type}:${uid}`;
}

function readState(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeState(key, state) {
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch { /* ignore quota */ }
}

function shouldShowPromo(state) {
  if (!state) return true;
  if (state.forever) return false;
  if (state.snoozeUntil && Number(state.snoozeUntil) > Date.now()) return false;
  return true;
}

/**
 * Dismissible “try new features” promo for تعبئة بالصوت.
 * Shown to company staff + merchants when the feature is enabled.
 * Close → hide for 3 days; “don’t show again” → permanent for this user.
 */
export default function VoiceFeaturePromo({ audience = 'company' }) {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [screens, setScreens] = useState(null);

  useEffect(() => {
    if (!user) return undefined;
    const key = storageKey(user);
    const stored = readState(key);
    if (!shouldShowPromo(stored)) {
      setVisible(false);
      return undefined;
    }

    let cancelled = false;
    api.getCompanySettings()
      .then((settings) => {
        if (cancelled) return;
        const enabled = !!(settings?.voice_command_enabled || settings?.voice_command?.enabled);
        if (!enabled) {
          setVisible(false);
          return;
        }
        const sc = settings?.voice_command?.screens || {};
        if (audience === 'merchant' && sc.merchant_new_order === 0) {
          setVisible(false);
          return;
        }
        if (audience === 'company' && sc.company_new_order === 0) {
          setVisible(false);
          return;
        }
        setScreens(sc);
        setReady(!!(settings?.voice_command_ready || settings?.voice_command?.ready));
        setVisible(true);
      })
      .catch(() => {
        if (!cancelled) setVisible(false);
      });

    return () => { cancelled = true; };
  }, [user, audience]);

  const dismiss = useCallback(() => {
    if (!user) return;
    const key = storageKey(user);
    if (dontShowAgain) {
      writeState(key, { forever: true, at: Date.now() });
    } else {
      writeState(key, { forever: false, snoozeUntil: Date.now() + SNOOZE_MS, at: Date.now() });
    }
    setVisible(false);
  }, [user, dontShowAgain]);

  if (!visible) return null;

  const ctaPath = audience === 'merchant' ? '/merchant/orders/new' : '/orders';
  const ctaLabel = audience === 'merchant' ? 'جرّب شحنة جديدة بالصوت' : 'جرّب إضافة شحنة بالصوت';

  return (
    <div className="vi-voice-promo" role="region" aria-label="ميزة تجريبية: تعبئة بالصوت">
      <div className="vi-voice-promo__glow" aria-hidden />
      <div className="vi-voice-promo__head">
        <div className="vi-voice-promo__titles">
          <span className="vi-voice-promo__kicker">✨ جرّب ميزة جديدة</span>
          <h3 className="vi-voice-promo__title">تعبئة الشحنة بالصوت</h3>
        </div>
        <button
          type="button"
          className="vi-voice-promo__close"
          onClick={dismiss}
          aria-label="إغلاق"
          title="إغلاق"
        >
          ✕
        </button>
      </div>

      <p className="vi-voice-promo__lead">
        تحدّث عن الشحنة — والنظام يحوّل كلامكم بذكاء إلى حقول جاهزة للمراجعة والحفظ.
        أنتم ضمن البرنامج التجريبي لفترة محدودة؛ اكتشفوا السرعة الجديدة لإنشاء الشحنات!
      </p>

      <ul className="vi-voice-promo__list">
        {audience === 'company' ? (
          <li>من «الشحنات» → إضافة شحنة: اذكروا التاجر والمستلم والمدينة والقيمة</li>
        ) : (
          <li>من «شحنة جديدة»: قولوا اسم المستلم والهاتف والمدينة والعنوان والقيمة</li>
        )}
        <li>
          {ready
            ? 'ابحثوا عن أيقونة الميكروفون في النموذج — سجّلوا ثم أرسلوا'
            : 'الميزة مفعّلة — قد يحتاج المدير إكمال إعداد الصوت من النظام'}
        </li>
        {screens?.merchant_new_order !== 0 && audience === 'company' && (
          <li>متاحة أيضاً لتجّاركم في بوابة التاجر</li>
        )}
      </ul>

      <div className="vi-voice-promo__actions">
        <Link to={ctaPath} className="vi-voice-promo__cta" onClick={dismiss}>
          {ctaLabel}
        </Link>
        <label className="vi-voice-promo__check">
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
          />
          <span>لا تظهر لي مرة أخرى</span>
        </label>
        <button type="button" className="vi-voice-promo__later" onClick={dismiss}>
          {dontShowAgain ? 'إغلاق نهائياً' : 'لاحقاً (إخفاء 3 أيام)'}
        </button>
      </div>
    </div>
  );
}
